/* Editorial formatters — these are opinionated. Long numbers get thin-space
   separators; days/hours pick the right unit; case IDs get monospaced; all
   plurals respect 1 vs. many. This is why we don't auto-generate the client. */

const THIN = "\u2009"; // thin space

export function formatCount(n: number | null | undefined): string {
  if (n == null) return "—";
  return n.toLocaleString("en-US").replace(/,/g, THIN);
}

export function formatBytes(n: number | null | undefined): string {
  if (n == null) return "—";
  if (n < 1024) return `${n} B`;
  const kb = n / 1024;
  if (kb < 1024) return `${kb.toFixed(kb < 10 ? 1 : 0)} KB`;
  const mb = kb / 1024;
  return `${mb.toFixed(mb < 10 ? 2 : 1)} MB`;
}

export function formatDays(d: number | null | undefined): string {
  if (d == null || Number.isNaN(d)) return "—";
  if (d < 1 / 24) return `${Math.round(d * 24 * 60)}${THIN}min`;
  if (d < 1) return `${(d * 24).toFixed(1)}${THIN}h`;
  if (d < 365) return `${d.toFixed(d < 10 ? 1 : 0)}${THIN}d`;
  const y = d / 365;
  return `${y.toFixed(y < 10 ? 1 : 0)}${THIN}yr`;
}

export function formatSeconds(s: number | null | undefined): string {
  if (s == null) return "—";
  if (s < 60) return `${s.toFixed(1)}${THIN}s`;
  const m = s / 60;
  return `${m.toFixed(1)}${THIN}min`;
}

export function formatPct(p: number | null | undefined): string {
  if (p == null) return "—";
  return `${p.toFixed(p < 10 ? 1 : 0)}%`;
}

/* Severity → glyph + canonical class. The glyph choices are intentional:
   '‡' double dagger for critical (used in scholarly footnotes for serious
   qualifications), '§' section mark for high (a structural warning), '†'
   dagger for medium, '·' middle-dot for low, '¶' pilcrow for info. */
export function severityGlyph(sev: string): string {
  const s = sev.toLowerCase();
  if (s === "critical") return "‡";
  if (s === "high") return "§";
  if (s === "medium") return "†";
  if (s === "low") return "·";
  return "¶";
}

export function canonicalSeverity(sev: string): string {
  const s = sev.toLowerCase();
  if (["critical", "high", "medium", "low", "info"].includes(s)) return s;
  return "info";
}

/* Roman numerals for dossier numbering — editorial flair. */
export function toRoman(n: number): string {
  const table: [number, string][] = [
    [1000, "M"], [900, "CM"], [500, "D"], [400, "CD"],
    [100, "C"], [90, "XC"], [50, "L"], [40, "XL"],
    [10, "X"], [9, "IX"], [5, "V"], [4, "IV"], [1, "I"],
  ];
  let out = "";
  let rem = n;
  for (const [v, sym] of table) {
    while (rem >= v) { out += sym; rem -= v; }
  }
  return out;
}
